import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Skeleton,
  Stack,
  Text,
  Table,
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import { Navigate, useParams } from 'react-router-dom'
import { fetchPrizes } from '../services/firebaseService'
import { EmptyState } from '../components/ui/EmptyState'
import { StatusBadge } from '../components/ui/StatusBadge'
import { useDocumentData } from '@kage1020/react-firebase-hooks/firestore'
import { doc } from 'firebase/firestore'
import { db } from '../firebase'
import { useEventTypes } from '../contexts/EventTypesContext'
import { useTranslation } from '../i18n'

export default function PrizeRedemptionPage() {
  const { eventId } = useParams()
  const { t } = useTranslation()
  const { getTypeConfig } = useEventTypes()
  const [prizes, setPrizes] = useState([])
  const [loading, setLoading] = useState(true)
  const [event] = useDocumentData(doc(db, 'events', eventId))

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      try {
        const data = await fetchPrizes(eventId)
        setPrizes(data)
      } catch (err) {
        console.error('Failed to fetch prizes:', err)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [eventId])

  if (event && getTypeConfig(event.eventType)?.capabilities?.prizes === false) {
    return <Navigate to={`/events/${eventId}`} replace />
  }

  const formatDate = (ts) => {
    if (!ts) return '—'
    const d = ts.toDate ? ts.toDate() : new Date(ts)
    return d.toLocaleString()
  }

  return (
    <Box>
      <Stack gap={4} mb={{ base: 0, lg: 6 }}>
        <Heading size={{ base: 'md', md: 'lg' }} srOnly={{ base: true, lg: false }} color="var(--mc-text)" fontFamily="'DM Sans', sans-serif">{t('prizes.title')}</Heading>
      </Stack>

      <Stack gap={4}>
        <Flex gap={4} wrap="wrap">
          <Badge bg="var(--mc-green-bg)" color="var(--mc-green)" px={2}>
            <HStack gap={1.5}><span className="status-dot status-dot--green" />{t('prizes.redeemed', { count: prizes.filter(p => p.status === 'redeemed').length })}</HStack>
          </Badge>
          <Badge bg="var(--mc-info-bg)" color="var(--mc-info)" px={2}>
            {t('prizes.active', { count: prizes.filter(p => p.status === 'active').length })}
          </Badge>
          <Badge bg="rgba(148, 163, 184, 0.1)" color="var(--mc-steel)" px={2}>
            {t('prizes.total', { count: prizes.length })}
          </Badge>
        </Flex>

        {loading ? (
          <Stack gap={2}>
            {[1, 2, 3].map(i => (
              <Skeleton key={i} height="40px" borderRadius="md" />
            ))}
          </Stack>
        ) : prizes.length === 0 ? (
          <EmptyState message={t('prizes.none')} />
        ) : (
          <Box overflowX="auto" borderRadius="md">
          <Table.Root size="sm" variant="outline">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader color="var(--mc-text-muted)" fontFamily="'JetBrains Mono', monospace" fontSize="xs">{t('prizes.columnUid')}</Table.ColumnHeader>
                <Table.ColumnHeader color="var(--mc-text-muted)" fontSize="xs">{t('prizes.columnStatus')}</Table.ColumnHeader>
                <Table.ColumnHeader color="var(--mc-text-muted)" fontSize="xs">{t('prizes.columnCreated')}</Table.ColumnHeader>
                <Table.ColumnHeader color="var(--mc-text-muted)" fontSize="xs">{t('prizes.columnRedeemed')}</Table.ColumnHeader>
                <Table.ColumnHeader color="var(--mc-text-muted)" fontFamily="'JetBrains Mono', monospace" fontSize="xs">{t('prizes.columnRedeemedBy')}</Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {prizes.map((prize) => (
                <Table.Row key={prize.id}>
                  <Table.Cell>
                    <Text fontSize="xs" fontFamily="'JetBrains Mono', monospace" color="var(--mc-steel)">{prize.uid}</Text>
                  </Table.Cell>
                  <Table.Cell>
                    <StatusBadge status={prize.status} />
                  </Table.Cell>
                  <Table.Cell>
                    <Text fontSize="xs" fontFamily="'JetBrains Mono', monospace" color="var(--mc-steel)">{formatDate(prize.createdAt)}</Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Text fontSize="xs" fontFamily="'JetBrains Mono', monospace" color="var(--mc-steel)">{formatDate(prize.redeemedAt)}</Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Text fontSize="xs" fontFamily="'JetBrains Mono', monospace" color="var(--mc-steel)">{prize.redeemedBy || '—'}</Text>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
          </Box>
        )}
      </Stack>
    </Box>
  )
}
